import { FailedMessagesProfileItemFilterModel } from '../../models/profile/failed-messages-profile-item.model';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { SimpleSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { FailedMessagesProfileItemService } from './failed-messages-profile-item.service';
import {
  FailedMessagesProfileItemLoad,
  FailedMessagesProfileItemCreate,
  FailedMessagesProfileItemUpdate,
  FailedMessagesProfileItemInitialState,
} from './failed-messages-profile-item.actions';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import { Error, CustomHttpErrorResponse } from '../../../../../../common';

export interface FailedMessagesProfileItemStateModel extends FailedMessagesProfileItemFilterModel {
  loaded: boolean;
}

const initialState: FailedMessagesProfileItemStateModel = {
  ...new FailedMessagesProfileItemFilterModel(),
  loaded: false,
};

@State<FailedMessagesProfileItemStateModel>({
  name: 'failedMessagesProfileItem',
  defaults: initialState,
})
@Injectable()
export class FailedMessagesProfileItemState {
  constructor(
    private failedMessagesProfileItemService: FailedMessagesProfileItemService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getProfileId(state: FailedMessagesProfileItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getState(state: FailedMessagesProfileItemStateModel): FailedMessagesProfileItemStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: FailedMessagesProfileItemStateModel): boolean {
    return state.loaded;
  }

  @Action(FailedMessagesProfileItemLoad)
  getProfileItem(
    ctx: StateContext<FailedMessagesProfileItemStateModel>,
    { payload }: FailedMessagesProfileItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.failedMessagesProfileItemService.getProfile(payload).pipe(
      map((profile: FailedMessagesProfileItemFilterModel) => {
        ctx.patchState({
          ...new FailedMessagesProfileItemFilterModel(profile),
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(FailedMessagesProfileItemCreate)
  createProfileItem(
    ctx: StateContext<FailedMessagesProfileItemStateModel>,
    { payload }: FailedMessagesProfileItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.failedMessagesProfileItemService.createProfile(payload).pipe(
      map((profile: FailedMessagesProfileItemFilterModel) => {
        ctx.patchState({
          ...profile,
          loaded: true,
        });
        this.notificationService.success('api.failedMessagesProfile.success.create');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(FailedMessagesProfileItemUpdate)
  updateProfileItem(
    ctx: StateContext<FailedMessagesProfileItemStateModel>,
    { payload }: FailedMessagesProfileItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.failedMessagesProfileItemService.updateProfile(payload).pipe(
      map((updatedProfile: FailedMessagesProfileItemFilterModel) => {
        ctx.patchState({ ...updatedProfile });
        this.notificationService.success('api.failedMessagesProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(FailedMessagesProfileItemInitialState)
  setInitialState(ctx: StateContext<FailedMessagesProfileItemStateModel>): void {
    ctx.setState(initialState);
  }
}
